import { useContext, useMemo } from 'react'
import { Keyboard, Pressable, Text, View } from 'react-native'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { router } from 'expo-router'
import { BigNumber } from 'mathjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { MAX_DEAL_AMOUNT, PaymentKind } from '@/constants/common'
import { formatLocalFiatAmount, formatMarketPrice, marketPresentation } from '@/config/marketPresentation'
import { CREATE_JBP_WITHDRAWAL_INVOICE, USER_CREATE_SELL_ORDER } from '@/constants/graphql/mutations'
import { GET_CURRENT_VIP_RECORD, GET_JBP_PAYMENT_METHOD_SETTINGS, GET_WALLETS } from '@/constants/graphql/queries'
import { CurrencyKind, Wallet } from '@/types/wallet'
import { getTradingDisabledMessage, isFunctionDisabledError } from '@/utils/graphqlError'
import {
  buildJbpIdempotencyKey,
  CurrentVipRecordQuery,
  formatJbpCurrency,
  formatJbpFeeLabel,
  formatJbpFeeSnapshotLabel,
  formatJbpRangeLabel,
  getEligibleJbpSettings,
  getJbpPaymentMethodLabel,
  JBP_WITHDRAWALS_ENABLED,
  JbpFeeMode,
  JbpPaymentMethodKind,
  JbpPaymentMethodSetting,
  JbpPaymentMethodSettingsQuery
} from '@/utils/jbp'
import { getBignumber, toCurrency } from '@/utils/numbers'
import {
  Button,
  FormScrollView,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  useModal,
  useToast
} from '@/components/base'
import DescriptionListItem from '@/components/DescriptionListItem'
import { NumberInput } from '@/components/form-control'
import { Info } from '@/components/icon'
import BrandLogo from '@/components/BrandLogo'
import { JbpVipConditions } from '@/components/jbp/JbpVipConditions'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import { SelectPaymentMethodContext, SelectPaymentMethodTrigger } from '@/components/SelectPaymentMethodScreen'

const BASE_PRICE = 1

type SellValues = {
  dealAmount: number
  minDealAmount: number
}

type SelectedSellPayment = {
  [key in PaymentKind]?: {
    id: string
    kind: PaymentKind
  }
}

type JbpWithdrawalInvoice = {
  invoiceId: string
  userId: number
  companyOrderNum: string
  mownecumOrderNum?: string | null
  bankId: string
  cardName: string
  cardNumMasked: string
  issueBankName?: string | null
  issueBankAddress?: string | null
  state: string
  requestedAmount: string
  paidAmount: string
  debitedAmount: string
  feeMode?: JbpFeeMode | null
  transactionFeeRate?: string | null
  transactionFeeFlat?: string | null
  transactionFeeAmount?: string | null
  exactTransactionCharge?: string | null
  statusMessage?: string | null
  callbackCount: number
  callbackVerified: boolean
  timeoutAt?: string | null
  closedAt?: string | null
  createdAt: string
  updatedAt: string
  settledAt?: string | null
}

type CreateJbpWithdrawalInvoiceMutation = {
  createJbpWithdrawalInvoice: JbpWithdrawalInvoice
}

const defaultValues: SellValues = {
  dealAmount: 0,
  minDealAmount: 0
}

export default function SellScreen() {
  const { t } = useTranslation()
  const client = useApolloClient()
  const loading = useLoadingIndicator()
  const toast = useToast()
  const { openModal, closeModal } = useModal()
  const { control, handleSubmit, reset, watch, setError } = useForm<SellValues>({ defaultValues })
  const {
    selectedSellPayment: selectedPayment,
    setSelectedSellPayment: setSelectedPayment,
    open,
    setType,
    isHidden,
    show
  } = useContext(SelectPaymentMethodContext)
  const selectedPaymentNum = Object.values(selectedPayment).filter((payment) => payment !== undefined).length

  const { data } = useQuery(GET_WALLETS, {
    fetchPolicy: 'network-only',
    pollInterval: 60000,
    onError: (error) => console.log(`get wallet failed, ${error}`)
  })
  const { data: currentVipRecordData } = useQuery<CurrentVipRecordQuery>(GET_CURRENT_VIP_RECORD)
  const { data: jbpPaymentSettingsData, loading: jbpPaymentSettingsLoading } = useQuery<JbpPaymentMethodSettingsQuery>(
    GET_JBP_PAYMENT_METHOD_SETTINGS,
    {
      skip: !JBP_WITHDRAWALS_ENABLED
    }
  )

  const [createSellOrder] = useMutation(USER_CREATE_SELL_ORDER, {
    onCompleted: (data) => {
      loading.stop()
      reset()
      setSelectedPayment({})
      setTimeout(() => {
        client.refetchQueries({ include: [GET_WALLETS] })
      }, 200)
      // Navigate to the order details screen
      router.push(`/order/sell/${data.userCreateSellOrder.internalKey}`)
    },
    onError: (error) => {
      loading.stop()
      console.log('error', error)
      toast.show({
        variant: 'error',
        description: isFunctionDisabledError(error) ? getTradingDisabledMessage() : t('c2c:serverError')
      })
    }
  })
  const [createJbpWithdrawalInvoice] = useMutation<CreateJbpWithdrawalInvoiceMutation>(CREATE_JBP_WITHDRAWAL_INVOICE)

  const availableBalance = data ? getAvailableBalance(data) : '−.−−'

  const shownDealAmount = watch('dealAmount') * BASE_PRICE
  const shownMinDealAmount = watch('minDealAmount')
  const currentVipLevel = currentVipRecordData?.getCurrentVipRecord?.level ?? 0
  const jbpPaymentMethodSettings = jbpPaymentSettingsData?.getJbpPaymentMethodSettings?.data ?? []
  const eligibleJbpSettings = useMemo(
    () =>
      JBP_WITHDRAWALS_ENABLED
        ? getEligibleJbpSettings(jbpPaymentMethodSettings, Number(shownDealAmount || 0), currentVipLevel)
        : [],
    [currentVipLevel, jbpPaymentMethodSettings, shownDealAmount]
  )
  const hasSelectedEligibleJbpPayment = useMemo(
    () => eligibleJbpSettings.some((setting) => selectedPayment[getPaymentKindForJbpMethod(setting.method)]),
    [eligibleJbpSettings, selectedPayment]
  )

  const startC2cSellOrder = (data: SellValues) => {
    const selectedPaymentIds: string[] = Object.values(selectedPayment)
      .map((payment) => payment?.id)
      .filter((payment) => payment !== undefined)
    console.log('selected', selectedPaymentIds)
    loading.start()
    createSellOrder({
      variables: {
        totalDealAmount: {
          kind: CurrencyKind[CurrencyKind.YBB],
          value: data.dealAmount.toString()
        },
        minDealAmount: {
          kind: CurrencyKind[CurrencyKind.YBB],
          value: data.minDealAmount.toString()
        },
        paymentMethodIds: selectedPaymentIds
      }
    })
  }

  const startJbpSellOrder = async (values: SellValues, setting: JbpPaymentMethodSetting, paymentMethodId: string) => {
    loading.start()
    try {
      const result = await createJbpWithdrawalInvoice({
        variables: {
          input: {
            amount: values.dealAmount.toString(),
            bankId: setting.bankId,
            paymentMethodId,
            idempotencyKey: buildJbpIdempotencyKey('mobile-c2c-sell'),
            memo: 'mobile-c2c-sell'
          }
        }
      })
      loading.stop()

      const invoice = result.data?.createJbpWithdrawalInvoice
      if (!invoice) {
        toast.show({ variant: 'error', description: t('c2c:serverError') })
        return
      }

      reset()
      setSelectedPayment({})
      setTimeout(() => {
        client.refetchQueries({ include: [GET_WALLETS] })
      }, 200)
      openModal({
        children: <JbpWithdrawalStatusModal invoice={invoice} onClose={closeModal} setting={setting} />,
        onClose: closeModal
      })
    } catch (error) {
      loading.stop()
      console.log('error', error)
      toast.show({ variant: 'error', description: t('c2c:fastTradingUnavailable') })
    }
  }

  const validateC2cSellValues = (values: SellValues) => {
    if (!values.minDealAmount || values.minDealAmount <= 0) {
      setError('minDealAmount', { message: t('c2c:minimumTransactionRequired') })
      return false
    }
    if (values.dealAmount < values.minDealAmount) {
      setError('minDealAmount', { message: t('c2c:minimumLessThanListed') })
      return false
    }

    return true
  }

  const onSubmit = async (data: SellValues) => {
    Keyboard.dismiss()
    const availableBalanceNum = Number(availableBalance.replace(',', ''))
    if (!Number.isNaN(availableBalanceNum) && data.dealAmount > availableBalanceNum) {
      setError('dealAmount', { message: t('c2c:insufficientBalance') })
      return
    }
    if (data.dealAmount > MAX_DEAL_AMOUNT) {
      setError('dealAmount', { message: t('c2c:listedQuantityLimitExceeded') })
      return
    }

    const eligibleSettings = JBP_WITHDRAWALS_ENABLED
      ? getEligibleJbpSettings(jbpPaymentMethodSettings, data.dealAmount, currentVipLevel)
      : []
    if (eligibleSettings.length > 0) {
      openModal({
        children: (
          <ExpressSellOptionsModal
            amount={data.dealAmount}
            onClose={closeModal}
            onUseC2c={() => {
              if (!validateC2cSellValues(data)) {
                closeModal()
                return
              }
              closeModal()
              startC2cSellOrder(data)
            }}
            onUseJbp={(setting, paymentMethodId) => {
              startJbpSellOrder(data, setting, paymentMethodId)
            }}
            selectedPayment={selectedPayment}
            settings={eligibleSettings}
            vipLevel={currentVipLevel}
          />
        ),
        onClose: closeModal
      })
      return
    }

    if (!validateC2cSellValues(data)) {
      return
    }
    startC2cSellOrder(data)
  }

  function onSelectPaymentMethod() {
    Keyboard.dismiss()
    if (isHidden) {
      show()
    }
    open()
    setType('sell')
  }

  return (
    <View className={'h-full'}>
      <FormScrollView className={'p-4'}>
        <View className={'rounded-lg border border-border-10 bg-background-primary p-4'}>
          <NumberInput
            control={control}
            name={'dealAmount'}
            label={t('orders:listedQuantity')}
            labelRightElement={
              <Text>
                <Text className={'text-xs text-text-tertiary'}>{t('c2c:availableBalance')}</Text>
                <Text className={'font-roboto-medium text-xs text-primary'}>{availableBalance}</Text>
              </Text>
            }
            type={'number'}
            autoCorrect={false}
            returnKeyType={'done'}
            keyboardType={'decimal-pad'}
            leftElement={<BrandLogo className={'ml-3 size-5 h-full'} />}
          />
          <View className={'h-2'} />
          <View className={'gap-1'}>
            <NumberInput
              control={control}
              name={'minDealAmount'}
              label={t('orders:minimumTransaction')}
              labelRightElement={
                <Pressable
                  className={'ml-[-88px] mr-auto pl-[92px]'}
                  onPress={() => {
                    openModal({ children: <MinDealAmountModal onClose={closeModal} />, onClose: closeModal })
                  }}
                >
                  <Info className={'size-5 text-text-quaternary'} />
                </Pressable>
              }
              labelClassName={'items-center'}
              type={'number'}
              autoCorrect={false}
              returnKeyType={'done'}
              keyboardType={'decimal-pad'}
              leftElement={<BrandLogo className={'ml-3 size-5 h-full'} />}
            />
          </View>
          <View className={'h-2'} />
          <SelectPaymentMethodTrigger
            type={'sell'}
            value={selectedPaymentNum === 0 ? '' : t('c2c:selectedReceivingMethods', { count: selectedPaymentNum })}
            onPress={onSelectPaymentMethod}
          />
          {JBP_WITHDRAWALS_ENABLED && !jbpPaymentSettingsLoading && (
            <>
              <View className={'h-2'} />
              <JbpVipConditions settings={jbpPaymentMethodSettings} vipLevel={currentVipLevel} />
            </>
          )}
          <View className={'h-2'} />
          <View className={'flex-row justify-between'}>
            <Text className={'text-sm text-text-secondary'}>{t('orders:price')}</Text>
            <Text
              className={'font-roboto-medium text-sm font-medium text-text-primary'}
            >{formatMarketPrice(BASE_PRICE)}</Text>
          </View>
          <View className={'h-4'} />
          <View className={'flex-row justify-between'}>
            <Text className={'text-sm text-text-secondary'}>{t('orders:expectedTotal')}</Text>
            <Text
              className={'font-roboto-medium text-sm text-primary'}
            >{formatLocalFiatAmount(shownDealAmount.toString())}</Text>
          </View>
          <View className={'h-4'} />
          <Text className={'text-sm font-medium text-text-warning'}>
            {t('c2c:securityReminder', { token: marketPresentation.platformToken.displayName })}
          </Text>
          <View className={'h-3'} />
          <Button
            size={'md'}
            onPress={handleSubmit(onSubmit)}
            disabled={
              !selectedPaymentNum ||
              !shownDealAmount ||
              (!shownMinDealAmount && !hasSelectedEligibleJbpPayment) ||
              loading.isLoading
            }
          >
            {t('c2c:sellToken', { token: marketPresentation.platformToken.displayName })}
          </Button>
        </View>
      </FormScrollView>
    </View>
  )
}

function ExpressSellOptionsModal({
  amount,
  onClose,
  onUseC2c,
  onUseJbp,
  selectedPayment,
  settings,
  vipLevel
}: {
  amount: number
  onClose: () => void
  onUseC2c: () => void
  onUseJbp: (setting: JbpPaymentMethodSetting, paymentMethodId: string) => void
  selectedPayment: SelectedSellPayment
  settings: JbpPaymentMethodSetting[]
  vipLevel: number
}) {
  const { t } = useTranslation()
  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('c2c:selectTradingMethod')}</ModalTitle>
      </ModalHeader>
      <ModalBody className={'items-stretch'}>
        <Text className={'text-sm text-text-secondary'}>
          {t('c2c:vipFastPayEligibility', { level: vipLevel, methodType: t('orders:paymentMethods') })}
        </Text>
        <View className={'h-3'} />
        <View className={'gap-y-2'}>
          {settings.map((setting) => {
            const payment = selectedPayment[getPaymentKindForJbpMethod(setting.method)]
            const disabled = !payment

            return (
              <Pressable
                className={`rounded-lg border border-border-10 bg-background-secondary p-3 ${disabled ? 'opacity-60' : ''}`}
                disabled={disabled}
                key={setting.method}
                onPress={() => payment && onUseJbp(setting, payment.id)}
              >
                <View className={'flex-row items-center justify-between'}>
                  <Text className={'text-sm font-medium text-text-primary'}>
                    {getJbpPaymentMethodLabel(setting.method)}
                  </Text>
                  <Text className={`text-xs ${disabled ? 'text-text-tertiary' : 'text-primary'}`}>
                    {disabled ? t('c2c:selectReceivingMethod') : t('orders:fastTrack')}
                  </Text>
                </View>
                <View className={'mt-2 flex-row justify-between'}>
                  <Text className={'text-xs text-text-secondary'}>{t('c2c:vipAvailability')}</Text>
                  <Text className={'font-roboto-medium text-xs text-text-primary'}>{formatJbpRangeLabel(setting)}</Text>
                </View>
                <View className={'mt-1 flex-row justify-between'}>
                  <Text className={'text-xs text-text-secondary'}>{t('orders:paymentAmount')}</Text>
                  <Text className={'font-roboto-medium text-xs text-text-primary'}>{formatJbpCurrency(amount)}</Text>
                </View>
                <View className={'mt-1 flex-row justify-between'}>
                  <Text className={'text-xs text-text-secondary'}>{t('c2c:fee')}</Text>
                  <Text className={'font-roboto-medium text-xs text-text-primary'}>
                    {formatJbpFeeLabel(setting, amount)}
                  </Text>
                </View>
              </Pressable>
            )
          })}
        </View>
      </ModalBody>
      <ModalFooter className={'flex-col items-stretch gap-y-2 p-4'}>
        <Button size={'md'} variant={'outline'} onPress={onUseC2c}>
          {t('c2c:continueStandardC2C')}
        </Button>
        <Button size={'md'} variant={'flat'} onPress={onClose}>
          {t('cancel')}
        </Button>
      </ModalFooter>
    </>
  )
}

function JbpWithdrawalStatusModal({
  invoice,
  onClose,
  setting
}: {
  invoice: JbpWithdrawalInvoice
  onClose: () => void
  setting: JbpPaymentMethodSetting
}) {
  const { t } = useTranslation()
  const isAccepted = invoice.state === 'AWAIT_SETTLEMENT' || invoice.state === 'COMPLETED'

  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('c2c:fastSell')}</ModalTitle>
      </ModalHeader>
      <ModalBody className={'items-stretch px-0'}>
        <View className={'px-4 pb-2'}>
          <Text className={'text-sm text-text-secondary'}>
            {t(isAccepted ? 'c2c:fastSellSubmitted' : 'c2c:fastSellFailed')}
          </Text>
        </View>
        <DescriptionListItem label={t('c2c:transactionChannel')} hasBorderTop={true}>
          {getJbpPaymentMethodLabel(setting.method)}
        </DescriptionListItem>
        <DescriptionListItem label={t('orders:paymentAmount')} isValuePrimary={true}>
          {formatJbpCurrency(invoice.requestedAmount)}
        </DescriptionListItem>
        <DescriptionListItem label={t('c2c:fee')}>{formatJbpFeeSnapshotLabel(invoice)}</DescriptionListItem>
        <DescriptionListItem label={t('c2c:receivingName')}>{invoice.cardName}</DescriptionListItem>
        <DescriptionListItem label={t('c2c:receivingAccount')}>{invoice.cardNumMasked}</DescriptionListItem>
        <DescriptionListItem label={t('orders:systemOrderNumber')} canCopy={true}>
          {invoice.companyOrderNum}
        </DescriptionListItem>
        {invoice.mownecumOrderNum && (
          <DescriptionListItem label={t('c2c:providerOrderNumber')} canCopy={true}>
            {invoice.mownecumOrderNum}
          </DescriptionListItem>
        )}
        {invoice.exactTransactionCharge && (
          <DescriptionListItem label={t('c2c:providerFee')}>
            {formatJbpCurrency(invoice.exactTransactionCharge)}
          </DescriptionListItem>
        )}
        <DescriptionListItem label={t('c2c:status')} hasBorderBottom={true}>
          {invoice.state}
        </DescriptionListItem>
        {invoice.statusMessage && (
          <DescriptionListItem label={t('c2c:remark')} hasBorderBottom={true}>
            {invoice.statusMessage}
          </DescriptionListItem>
        )}
      </ModalBody>
      <ModalFooter className={'p-4'}>
        <Button className={'flex-1'} size={'md'} onPress={onClose}>
          {t('c2c:close')}
        </Button>
      </ModalFooter>
    </>
  )
}

function getPaymentKindForJbpMethod(method: JbpPaymentMethodKind) {
  switch (method) {
    case 'BANK_CARD':
      return PaymentKind.bank
    case 'ALIPAY':
      return PaymentKind.alipay
    case 'WECHAT':
      return PaymentKind.wechat
  }
}

function getAvailableBalance(data: Record<string, any>) {
  const newAvailableBalance =
    data?.getWallets?.reduce((acc: BigNumber, wallet: Wallet) => {
      const balance = getBignumber(wallet.balance.value)
      return acc.add(balance)
    }, getBignumber(0)) ?? getBignumber(0)
  return toCurrency(newAvailableBalance.toString())
}

function MinDealAmountModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('orders:minimumTransaction')}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Text className={'text-sm text-text-secondary'}>{t('c2c:minimumTransactionHint')}</Text>
      </ModalBody>
      <ModalFooter>
        <Button className={'flex-1'} size={'md'} onPress={onClose}>
          {t('c2c:gotIt')}
        </Button>
      </ModalFooter>
    </>
  )
}
